import { requestIdleCallback } from "@theme/utilities";

/**
 * Handles lazy loading of hover images on resource cards.
 * Images with data-src/data-srcset are only loaded when the user hovers over the card.
 */
class HoverImageLoader {
  /** @type {Set<HTMLImageElement>} */
  #loadedImages = new Set();

  constructor() {
    this.#init();
  }

  #init() {
    // Use event delegation to handle hover on resource cards
    document.addEventListener(
      "pointerenter",
      this.#handlePointerEnter.bind(this),
      true
    );

    // Defer loading of card-gallery hover images
    this.#deferCardGalleryImages();

    // Eager load images in first visible row (including hover images)
    this.#eagerLoadFirstRow();

    // Preload hover images for all currently visible cards immediately
    this.#preloadVisibleCards();

    // Preload hover images when primary images finish loading
    this.#preloadOnPrimaryImageLoad();

    // Aggressive preloading for cards near viewport
    if ("IntersectionObserver" in window) {
      this.#observeNearViewport();
    }
  }

  /**
   * Defers loading of images in card-gallery slideshows (hover images)
   */
  #deferCardGalleryImages() {
    const deferImages = () => {
      const cardGalleries = document.querySelectorAll(".card-gallery");

      cardGalleries.forEach((gallery) => {
        const slides = gallery.querySelectorAll("slideshow-slide");
        if (slides.length < 2) return; // No hover image to defer

        // Get all images in slides with aria-hidden="true" (hidden slides)
        slides.forEach((slide, index) => {
          const isHidden = slide.getAttribute("aria-hidden") === "true";
          const images = slide.querySelectorAll("img");

          images.forEach((img) => {
            // Defer loading of second slide (hover image) and beyond for products not in first row
            if (
              isHidden &&
              img instanceof HTMLImageElement &&
              img.src &&
              index === 1
            ) {
              // Only defer the second slide (hover image), keep others as-is for now
              if (!img.hasAttribute("data-src")) {
                img.setAttribute("data-src", img.src);
                img.setAttribute("data-srcset", img.srcset || "");
                img.setAttribute("data-sizes", img.sizes || "");
                img.removeAttribute("src");
                img.removeAttribute("srcset");
                img.loading = "lazy";
              }
            }
          });
        });
      });
    };

    // Run after DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", deferImages);
    } else {
      requestIdleCallback(deferImages);
    }

    // Re-run when new galleries are added (infinite scroll)
    const mutationObserver = new MutationObserver(() => {
      requestIdleCallback(deferImages);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Eager loads images for products in the first visible row (including hover images)
   */
  #eagerLoadFirstRow() {
    const eagerLoadImages = () => {
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) return;

      const productItems = Array.from(
        productGrid.querySelectorAll(".product-grid__item")
      );
      if (productItems.length === 0) return;

      // Get the first item's position to determine row height
      const firstItem = productItems[0];
      if (!firstItem) return;

      const firstItemRect = firstItem.getBoundingClientRect();
      const firstRowTop = firstItemRect.top;
      const firstRowBottom = firstItemRect.bottom;
      const rowThreshold = (firstRowBottom - firstRowTop) * 0.5; // 50% overlap threshold

      // Find all items in the first row (within threshold of first item's top)
      const firstRowItems = productItems.filter((item) => {
        const rect = item.getBoundingClientRect();
        return Math.abs(rect.top - firstRowTop) < rowThreshold;
      });

      // Eager load images in first row (max 6 items to be safe)
      firstRowItems.slice(0, 6).forEach((item) => {
        // Eager load primary images in card-gallery
        const primaryImages = item.querySelectorAll(
          '.card-gallery slideshow-slide[aria-hidden="false"] img, .resource-card__image:not(.resource-card__image--secondary)[loading="lazy"]'
        );
        primaryImages.forEach((img) => {
          if (img instanceof HTMLImageElement) {
            img.loading = "eager";
            img.fetchPriority = "high";
          }
        });

        // Preload hover images for first row immediately
        this.#preloadHoverImageForItem(item, true);

        // Use link prefetch for first row hover images
        this.#prefetchHoverImageForItem(item);
      });
    };

    // Run immediately after DOM is ready (not on idle)
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", eagerLoadImages);
    } else {
      // Use requestAnimationFrame to ensure layout is complete, but don't wait for idle
      requestAnimationFrame(eagerLoadImages);
    }

    // Re-run when new items are added (infinite scroll)
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(eagerLoadImages);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Preloads hover image for a specific product item
   * @param {Element} item - The product grid item
   * @param {boolean} highPriority - Whether to use high fetch priority
   */
  #preloadHoverImageForItem(item, highPriority = false) {
    // Handle resource-card hover images
    const resourceCard = item.querySelector(".resource-card");
    if (resourceCard) {
      const hoverImage = resourceCard.querySelector(
        ".resource-card__image--secondary[data-src]"
      );
      if (hoverImage instanceof HTMLImageElement) {
        this.#loadHoverImage(hoverImage, highPriority);
      }
    }

    // Handle card-gallery hover images (second slide)
    const cardGallery = item.querySelector(".card-gallery");
    if (cardGallery) {
      const secondSlide = cardGallery.querySelector(
        "slideshow-slide:nth-child(2)"
      );
      if (secondSlide) {
        const images = secondSlide.querySelectorAll("img[data-src]");
        images.forEach((img) => {
          if (img instanceof HTMLImageElement) {
            this.#loadHoverImage(img, highPriority);
          }
        });
      }
    }
  }

  /**
   * Uses link prefetch to hint browser about hover images for first row
   * @param {Element} item - The product grid item
   */
  #prefetchHoverImageForItem(item) {
    // Get hover image URL
    const resourceCard = item.querySelector(".resource-card");
    const cardGallery = item.querySelector(".card-gallery");

    let hoverImageUrl = null;

    if (resourceCard) {
      const hoverImage = resourceCard.querySelector(
        ".resource-card__image--secondary[data-src]"
      );
      if (hoverImage) {
        hoverImageUrl = hoverImage.getAttribute("data-src");
      }
    } else if (cardGallery) {
      const secondSlide = cardGallery.querySelector(
        "slideshow-slide:nth-child(2) img[data-src]"
      );
      if (secondSlide) {
        hoverImageUrl = secondSlide.getAttribute("data-src");
      }
    }

    if (
      hoverImageUrl &&
      !document.querySelector(`link[rel="prefetch"][href="${hoverImageUrl}"]`)
    ) {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "image";
      link.href = hoverImageUrl;
      document.head.appendChild(link);
    }
  }

  /**
   * Preloads hover images for all currently visible cards immediately
   */
  #preloadVisibleCards() {
    const preloadVisible = () => {
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) return;

      const productItems = Array.from(
        productGrid.querySelectorAll(".product-grid__item")
      );

      productItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

        if (isVisible) {
          // Preload hover images for visible items immediately
          this.#preloadHoverImageForItem(item, false);
        }
      });
    };

    // Run after DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", preloadVisible);
    } else {
      requestAnimationFrame(preloadVisible);
    }

    // Re-run when new items are added (infinite scroll)
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(preloadVisible);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Preloads hover images when primary images finish loading
   */
  #preloadOnPrimaryImageLoad() {
    const setupPrimaryImageListeners = () => {
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) return;

      const productItems = Array.from(
        productGrid.querySelectorAll(".product-grid__item")
      );

      productItems.forEach((item) => {
        // Get primary image
        const primaryImage = item.querySelector(
          '.card-gallery slideshow-slide[aria-hidden="false"] img, .resource-card__image:not(.resource-card__image--secondary)'
        );

        if (primaryImage instanceof HTMLImageElement) {
          // If already loaded, preload hover image immediately
          if (primaryImage.complete && primaryImage.naturalHeight > 0) {
            this.#preloadHoverImageForItem(item, false);
          } else {
            // Otherwise, wait for load event
            primaryImage.addEventListener(
              "load",
              () => {
                this.#preloadHoverImageForItem(item, false);
              },
              { once: true }
            );
          }
        }
      });
    };

    // Run after DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", setupPrimaryImageListeners);
    } else {
      requestAnimationFrame(setupPrimaryImageListeners);
    }

    // Re-run when new items are added (infinite scroll)
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(setupPrimaryImageListeners);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Handles pointer enter events on resource cards and card galleries
   * @param {PointerEvent} event
   */
  #handlePointerEnter(event) {
    // Only handle mouse pointer events, not touch
    if (event.pointerType !== "mouse") return;

    const target = /** @type {Element} */ (event.target);

    // Handle resource-card hover images
    const resourceCard = target.closest(".resource-card");
    if (resourceCard) {
      const hoverImage = resourceCard.querySelector(
        ".resource-card__image--secondary[data-src]"
      );
      if (hoverImage instanceof HTMLImageElement) {
        this.#loadHoverImage(hoverImage);
        return;
      }
    }

    // Handle card-gallery hover images (second slide)
    const cardGallery = target.closest(".card-gallery");
    if (cardGallery) {
      const secondSlide = cardGallery.querySelector(
        "slideshow-slide:nth-child(2)"
      );
      if (secondSlide) {
        const images = secondSlide.querySelectorAll("img[data-src]");
        images.forEach((img) => {
          if (img instanceof HTMLImageElement) {
            this.#loadHoverImage(img);
          }
        });
      }
    }
  }

  /**
   * Loads a hover image by setting src/srcset from data attributes
   * @param {HTMLImageElement} image
   * @param {boolean} highPriority - Whether to use high fetch priority
   */
  #loadHoverImage(image, highPriority = false) {
    // Skip if already loaded
    if (this.#loadedImages.has(image)) return;
    if (image.src && image.src !== "") return;

    const dataSrc = image.getAttribute("data-src");
    const dataSrcset = image.getAttribute("data-srcset");
    const dataSizes = image.getAttribute("data-sizes");

    if (!dataSrc) return;

    // Set fetchpriority for faster loading
    if (highPriority) {
      image.fetchPriority = "high";
    }

    // Set src and srcset to trigger image load
    image.src = dataSrc;
    if (dataSrcset) {
      image.srcset = dataSrcset;
    }
    if (dataSizes) {
      image.sizes = dataSizes;
    }

    // Mark as loaded
    this.#loadedImages.add(image);
  }

  /**
   * Aggressively observes cards near viewport and preloads hover images immediately
   */
  #observeNearViewport() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const item = entry.target;

            // Preload immediately, not on idle callback
            this.#preloadHoverImageForItem(item, false);
          }
        });
      },
      {
        rootMargin: "400px", // Start loading when 400px away from viewport (more aggressive)
      }
    );

    // Observe all product items
    const observeItems = () => {
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) return;

      const productItems = Array.from(
        productGrid.querySelectorAll(".product-grid__item")
      );
      productItems.forEach((item) => observer.observe(item));
    };

    // Initial observation
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", observeItems);
    } else {
      requestAnimationFrame(observeItems);
    }

    // Re-observe when new items are added (for infinite scroll)
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(observeItems);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

// Initialize on page load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new HoverImageLoader();
  });
} else {
  new HoverImageLoader();
}
